import { useEffect, useMemo, useState } from "react";
import { Button, Upload, type UploadProps, type UploadUserFile } from "zhihao-ui-react";
import type { File as FileEntity } from "@zhihaoscm/entity";
import { fileService } from "@/api/services/fileService";
import { waybillService } from "@/api/services/waybillService";

export interface SingleFileUploadProps {
    modelValue?: number;
    file?: FileEntity;
    size?: number;
    icon?: boolean;
    tip?: string;
    acceptType?: string[];
    limitFileTypes?: string[];
    typesErrorMessage?: string;
    type?: number;
    isNotShowLoading?: boolean;
    canDelete?: boolean;
    uploadUsername?: string;
    businessId?: string;
    className?: string;
    isNeedWatermark?: boolean;
    /** 使用 zhihao-ui-react Upload 的通用文件列表，适用于需要统一文件项视觉的业务页面。 */
    showFileList?: boolean;
    onUpdateModelValue?: (value: number | undefined) => void;
    onUpdateFile?: (value: FileEntity | undefined) => void;
    onDelete?: (value: number | undefined) => void;
    validateFile?: () => void;
}

/** 将旧 Vue ElMessage 的上传校验提示桥接到 React 全局消息，同时保留可测试事件。 */
export function notifyUploadValidationError(message: string) {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent("zh-upload-error", { detail: message }));
    window.dispatchEvent(
        new CustomEvent("zhihao:global-message", {
            detail: { message, type: "warning" }
        })
    );
}

/** 单文件上传公共实现，兼容旧版 modelValue/file 双向数据和预览删除入口。 */
export default function SingleFileUpload({
    modelValue,
    file,
    size = 10,
    icon = false,
    tip = "上传",
    acceptType,
    limitFileTypes,
    typesErrorMessage,
    type = 1,
    canDelete = true,
    businessId,
    className,
    isNeedWatermark = false,
    showFileList = false,
    onUpdateModelValue,
    onUpdateFile,
    onDelete,
    validateFile
}: SingleFileUploadProps) {
    const [entity, setEntity] = useState<FileEntity | undefined>(file);
    const accepted = limitFileTypes ?? acceptType ?? ["pdf", "ofd", "jpeg", "png", "xml", "xls", "xlsx"];
    const fileList = useMemo<UploadUserFile[]>(
        () => (entity ? [{ name: entity.name, url: entity.path, response: entity }] : []),
        [entity]
    );
    useEffect(() => {
        if (!modelValue) {
            setEntity(undefined);
            onUpdateFile?.(undefined);
            return;
        }
        if (entity?.id === modelValue) return;
        fileService.getFileById(modelValue).then(next => {
            setEntity(next);
            onUpdateFile?.(next);
        });
    }, [modelValue]);
    const beforeUpload: UploadProps["beforeUpload"] = rawFile => {
        const suffix = rawFile.name.split(".").pop()?.toLowerCase() || "";
        const validType = accepted.some(
            item =>
                item.toLowerCase().replace(/^\./, "") === suffix || item.toLowerCase() === rawFile.type.toLowerCase()
        );
        if (!validType || rawFile.size / 1024 / 1024 > size) {
            // 旧版 single-file-upload 的超限提示固定为“文件大小超过限制”，不能只显示组合校验文案。
            notifyUploadValidationError(
                !validType ? typesErrorMessage || `仅支持 ${accepted.join(",")} 文件` : "文件大小超过限制"
            );
            return false;
        }
        return true;
    };
    const httpRequest: UploadProps["httpRequest"] = async options => {
        const formData = new FormData();
        formData.append("file", options.file);
        formData.append("fileName", options.file.name);
        const next =
            businessId && isNeedWatermark
                ? ((await waybillService.uploadWatermark(businessId, formData)) as FileEntity)
                : await fileService.uploadFile(formData);
        setEntity(next);
        onUpdateFile?.(next);
        onUpdateModelValue?.(next.id);
        validateFile?.();
        options.onSuccess(next);
    };
    const remove = () => {
        setEntity(undefined);
        onUpdateFile?.(undefined);
        onUpdateModelValue?.(undefined);
        onDelete?.(modelValue);
    };
    const preview = () => entity?.path && window.open(entity.path, "_blank", "noopener,noreferrer");
    // 公共文件列表有文件时隐藏上传触发器，保持 Vue single-upload 的单文件交互。
    const uploadClassName = [className, showFileList && entity ? "has-file" : undefined].filter(Boolean).join(" ");
    return (
        <Upload
            action="#"
            accept={accepted.map(item => (item.startsWith(".") ? item : `.${item}`)).join(",")}
            className={uploadClassName || undefined}
            limit={1}
            fileList={fileList}
            // 仅在启用通用文件列表时接入列表操作，避免改变其他调用方的默认行为。
            {...(showFileList
                ? {
                      onPreview: (fileItem: UploadUserFile) => {
                          if (fileItem.url) window.open(fileItem.url, "_blank", "noopener,noreferrer");
                      },
                      onRemove: () => remove()
                  }
                : {})}
            showFileList={showFileList}
            beforeUpload={beforeUpload}
            httpRequest={httpRequest}
        >
            {entity && !showFileList ? (
                <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
                    <span>{entity.name}</span>
                    <Button
                        size="small"
                        onClick={event => {
                            event.stopPropagation();
                            preview();
                        }}
                    >
                        查看/下载
                    </Button>
                    {(canDelete || type === 1) && (
                        <Button
                            size="small"
                            onClick={event => {
                                event.stopPropagation();
                                remove();
                            }}
                        >
                            删除
                        </Button>
                    )}
                </span>
            ) : entity ? null : (
                // 旧 Vue single-file-upload 使用 primary + link，确保上传入口继承主题蓝色。
                <Button color="primary" type="link">
                    {icon ? (
                        <span aria-hidden="true" className="iconfont icon-a-Frame30" style={{ marginRight: 5 }} />
                    ) : null}
                    {tip}
                </Button>
            )}
        </Upload>
    );
}
